import { Button, IconButton } from '@/common'
import {
  CaretDownIcon,
  CheckCircleIcon,
  CheckHalfDashedCircleIcon,
  DashedCircleIcon
} from '@/common/components/icons'
import { Transition } from '@headlessui/react'
import { useCallback, useState } from 'react'

type ToDoItem = {
  id: string
  title: string
  isCompleted: boolean
  actionButton: string
  action: () => void
  content?: React.ReactNode
  disabled?: boolean
}

function ToDoWrapper({ children }: { children: React.ReactNode }) {
  return <div className="w-full border border-dashed border-green-600 bg-green-300">{children}</div>
}

function ToDoTitle({ children }: { children: string }) {
  return (
    <div className="flex items-center space-x-2 p-4">
      <CheckHalfDashedCircleIcon className="size-6" />
      <h4 className="font-semibold text-lg">{children}</h4>
    </div>
  )
}

function ToDoListItem({ children }: { children: React.ReactNode }) {
  return <div className="border bg-background-surface">{children}</div>
}

function ToDoListItemHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-4 bg-background-surfrace face flex items-center justify-between space-x-2">
      {children}
    </div>
  )
}

function ToDoListItemActions({
  id,
  actionButton,
  action,
  hasContent,
  disabled,
  isOpen,
  onCaretDownClick
}: {
  id: string
  actionButton: string
  action: () => void
  hasContent: boolean
  disabled?: boolean
  isOpen: boolean
  onCaretDownClick: (id: string) => void
}) {
  return (
    <div className="flex items-center space-x-2">
      <Button onClick={action} disabled={disabled}>
        {actionButton}
      </Button>
      {hasContent && (
        <IconButton
          variant="txt-default"
          onClick={() => onCaretDownClick(id)}
          icon={
            <CaretDownIcon
              className={`transition-transform duration-150 ease-linear origin-center ${isOpen ? 'rotate-180' : ''}`}
            />
          }
        />
      )}
    </div>
  )
}

function ToDoListItemTitle({
  title,
  isCompleted,
  index,
  isIndexed
}: {
  title: string
  isCompleted: boolean
  index: number
  isIndexed: boolean
}) {
  return (
    <div className="flex items-center space-x-2">
      {isCompleted ? (
        <CheckCircleIcon className="size-6 text-green-600" />
      ) : (
        <DashedCircleIcon className="size-6" />
      )}

      <h4 className="font-medium text-lg">
        {isIndexed && <span>{index + 1}.</span>} {title}
      </h4>
    </div>
  )
}

export default function ToDo({
  items,
  title,
  indexed = true
}: {
  items: ToDoItem[]
  title: string
  indexed?: boolean
}) {
  const [openItems, setOpenItems] = useState<string[]>([])

  const toggleContent = useCallback(
    (id: string) => {
      setOpenItems(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]))
    },
    [setOpenItems]
  )

  return (
    <ToDoWrapper>
      <ToDoTitle>{title}</ToDoTitle>
      <div className="space-y-1 px-4 pb-4">
        {items.map((item, index) => {
          return (
            <ToDoListItem key={item.id}>
              <ToDoListItemHeader>
                <ToDoListItemTitle
                  title={item.title}
                  isCompleted={item.isCompleted}
                  index={index}
                  isIndexed={indexed}
                />
                <ToDoListItemActions
                  id={item.id}
                  actionButton={item.actionButton}
                  action={item.action}
                  hasContent={!!item.content}
                  disabled={item.disabled}
                  isOpen={openItems.includes(item.id)}
                  onCaretDownClick={toggleContent}
                />
              </ToDoListItemHeader>
              <Transition
                show={openItems.includes(item.id)}
                enter="transition-all duration-150 ease-linear"
                enterFrom="opacity-0 transform -translate-y-2"
                enterTo="opacity-100 transform translate-y-0"
                leave="transition-all duration-100 ease-linear"
                leaveFrom="opacity-100 transform translate-y-0"
                leaveTo="opacity-0 transform -translate-y-2"
              >
                <div className="p-4 border-t text-text-secondary0">{item.content}</div>
              </Transition>
            </ToDoListItem>
          )
        })}
      </div>
    </ToDoWrapper>
  )
}
